import { useEffect, useMemo, useState } from 'react';
import { jstDate, type DailyReport, type RollCall, type RollCallDetailDoc, type WorkHours } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { fmtTime } from '../lib/format';
import { toCsv } from '../lib/csv';
import { useDirectory } from '../lib/directory';
import { rollCallDetails } from '../lib/fs';
import { useStepUp } from '../components/StepUp';

type Tab = 'roll' | 'daily' | 'hours';
const hm = (min: number | null) => (min === null ? '—' : `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`);
const RC = { waiting: '確認待ち', confirmed: '確認済み', remeasure: '再測定', rejected: '乗務させない', not_done: '未実施' } as const;

/** 記録・印刷：点呼記録簿・運転日報・労働時間（仕様書「運行記録の印刷」） */
export function RecordsPage() {
  const today = jstDate(new Date().toISOString());
  const [tab, setTab] = useState<Tab>('roll');
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  type Data = { tab: 'roll'; rollCalls: RollCall[]; det: Map<string, RollCallDetailDoc> } | { tab: 'daily'; reports: DailyReport[] } | { tab: 'hours'; rows: WorkHours[] };
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState('');
  const dir = useDirectory();
  const { ensureRecent } = useStepUp();

  // 読み込みは条件が変わったときだけ。遅れて届いた前の結果は捨てる（名前は下で名簿から引くので、名簿が変わっても読み直さない）
  useEffect(() => {
    let alive = true;
    setData(null); setErr('');
    const fail = (e: unknown) => { if (alive) setErr(e instanceof HttpError ? e.message : '読み込めませんでした'); };
    const put = (d: Data) => { if (alive) setData(d); };
    // 測定値・健康状態・指示事項は Firestore の rollCallDetails から引く
    if (tab === 'roll') api.get<{ rollCalls: RollCall[] }>(`/v1/office/records/roll-calls?date=${date}`).then(async (r) => {
      // 点呼の中身を見るには、5 分以内に 6 桁を入れ直す
      const det = r.rollCalls.length && alive && (await ensureRecent('点呼記録簿（測定値・健康状態）を見る')) ? await rollCallDetails(r.rollCalls.map((x) => x.id)) : new Map<string, RollCallDetailDoc>();
      put({ tab: 'roll', rollCalls: r.rollCalls, det });
    }).catch(fail);
    if (tab === 'daily') api.get<{ reports: DailyReport[] }>(`/v1/office/records/daily-reports?date=${date}`).then((r) => put({ tab: 'daily', reports: r.reports })).catch(fail);
    if (tab === 'hours') api.get<{ rows: WorkHours[] }>(`/v1/office/records/work-hours?month=${month}`).then((r) => put({ tab: 'hours', rows: r.rows })).catch(fail);
    return () => { alive = false; };
  }, [tab, date, month, ensureRecent]);

  const table = useMemo((): { head: string[]; rows: string[][] } | null => {
    if (!data || data.tab !== tab) return null;
    const driver = (id: string) => dir.drivers.get(id)?.name ?? '—';
    const staff = (id: string | null) => (id && dir.staff.get(id)?.name) || '—';
    if (data.tab === 'roll') return {
      head: ['時刻', '運転者', '車両', '区分', '測定値', '酒気帯び', '健康状態', '日常点検', '検知器', '確認者', '指示事項', '状態'],
      rows: data.rollCalls.map((x) => { const d = data.det.get(x.id); return [fmtTime(x.at), driver(x.driverId), x.vehicle, x.kind === 'before' ? '乗務前' : '乗務後', d ? d.alcohol.toFixed(2) : '—', x.alcoholDetected ? '有' : '無',
        d?.healthOk == null ? '—' : d.healthOk ? '良' : `要注意（${d.healthNote ?? ''}）`, x.kind === 'after' ? '—' : d?.inspectionOk == null ? '—' : d.inspectionOk ? '済' : '未',
        x.detector ? `${x.detector}${x.detectorChecked ? '（動作確認済み）' : ''}` : '—', staff(x.checkedBy), d?.instruction ?? '—', RC[x.status]]; }),
    };
    if (data.tab === 'daily') return {
      head: ['運転者', '車両', '始業', '終業', '拘束時間', '走行距離', '運転', '休憩', '荷待ち', '配送', '乗務前の確認者', '乗務後の確認者'],
      rows: data.reports.map((x) => [driver(x.driverId), x.vehicle, fmtTime(x.workStartAt), x.workEndAt ? fmtTime(x.workEndAt) : '（勤務中）', hm(x.restraintMin), `${x.distanceKm} km`, hm(x.drivingMin), hm(x.breakMin), hm(x.waitMin), `${x.deliveries} 件`, staff(x.checkedBefore), staff(x.checkedAfter)]),
    };
    return {
      head: ['運転者', '勤務日数', '拘束時間（月）', '上限', '残り', '最長の 1 日', '休息 9 時間未満'],
      rows: data.rows.map((x) => [driver(x.driverId), `${x.days} 日`, hm(x.restraintMin), hm(x.limitMin), hm(Math.max(0, x.limitMin - x.restraintMin)), hm(x.longestDayMin), `${x.shortRests} 回`]),
    };
  }, [data, tab, dir]);

  const title = { roll: '点呼記録簿（アルコールチェック）', daily: '運転日報', hours: `労働時間（${month.replace('-', ' 年 ')} 月）` }[tab];
  const csv = async () => {
    if (!table || !(await ensureRecent('CSV の書き出し'))) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([toCsv([table.head, ...table.rows])], { type: 'text/csv' }));
    a.download = `${title.replace(/[（）\s]/g, '')}_${tab === 'hours' ? month : date}.csv`;
    a.click();
  };
  return (
    <div className="page">
      <div className="head no-print" style={{ justifyContent: 'space-between' }}>
        <div role="tablist" style={{ display: 'flex', gap: 6 }}>
          {(['roll', 'daily', 'hours'] as const).map((k) => <button key={k} role="tab" aria-selected={tab === k} className={`btn sm ${tab === k ? 'pri' : ''}`} onClick={() => setTab(k)}>{{ roll: '点呼記録簿', daily: '運転日報', hours: '労働時間' }[k]}</button>)}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {tab === 'hours'
            ? <input type="month" aria-label="月" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 170 }} />
            : <input type="date" aria-label="日付" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 170 }} />}
          <button className="btn" onClick={csv} disabled={!table}>CSV</button>
          <button className="btn pri" onClick={() => window.print()} disabled={!table}>印刷</button>
        </div>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
      <article className="paper">
        <div className="paper-h"><b>{title}</b><span>札幌本社　{tab === 'hours' ? month : date}</span></div>
        <table>
          <thead><tr>{(table?.head ?? []).map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {table === null ? <tr><td className="muted">読み込み中…</td></tr>
              : table.rows.length === 0 ? <tr><td colSpan={table.head.length} className="muted">この期間の記録はありません</td></tr>
              : table.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
          </tbody>
        </table>
        {tab === 'roll' && <p style={{ fontSize: 12 }}>確認方法：アルコール検知器を使った対面確認</p>}
        <div className="paper-f"><span>印刷 {new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}</span><span>運行管理者　＿＿＿＿＿＿　印</span></div>
      </article>
    </div>
  );
}
